import { DownOutlined, TableOutlined } from '@ant-design/icons';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultInvoiceTemplateOptions,
  defaultPayrollFreeMethod,
  invoiceMethodOptions,
  invoicePaymentModelOptions,
  paymentModels,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import {
  PAYMENT_MODEL_ENUM,
  PayrollFeeMethodEnum,
} from '@nxsys/constants/AppEnums';
import { useRaiseInvoice } from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { RaiseInvoiceParams } from '@nxsys/types/api';
import { Option } from '@nxsys/types/models/Common';
import { Form, Tooltip } from 'antd';
import { FormProps } from 'antd/lib';
import { map, toNumber } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import TieredFeeTableModal, { TieredFee } from './TieredFeeTableModal';
import AppInput from '@nxsys/components/AppInput';
import {
  defaultInvoiceRaisedAfterOptions,
  defaultInvoiceRaisedMethodOptions,
  defaultRuleInvoicingMethod,
  showCurrencyByCountry,
} from '@nxsys/utils';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import clsx from 'clsx';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import {
  afterDraftFeeMethodRule,
  invoiceDueDateRule,
  payrollFeeMethodOptions,
  tieredFeesRule,
} from '@nxsys/utils/formFieldValidation';
import { showMessage } from '@nxsys/utils/message';

type ModalProps = {
  isOpen: boolean;
  toggle: () => void;
  refetch: () => void;
};

type FormValues = {
  companyRef: Option<string> | null;
  agencyRef: Option<string> | null;
  raiseInvoicePEO: boolean;
  invoicedDate: string;
  periodFrom: string;
  periodTo: string;
  invoiceRaisedAfter: Option<string> | null;
  invoiceRaisedMethod: Option<string> | null;
  dueDate: string;
  rule: Option<string> | null;
  invoiceForSalary: boolean;
  vatSalaryRate: number;
  vatSalaryRateCode: Option<string> | null;
  invoiceForSalaryDescription: string;
  invoiceForERNI: boolean;
  erNIPercent: number;
  vatERNIRate: number;
  vatERNIRateCode: Option<string> | null;
  invoiceForERNIDescription: string;
  invoiceERPension: boolean;
  vatERPensionRate: number;
  vatERPensionRateCode: Option<string> | null;
  invoiceERPensionDescription: string;
  invoiceAppLevy: boolean;
  vatAppLevyRate: number;
  vatAppLevyRateCode: Option<string> | null;
  invoiceAppLevyDescription: string;
  invoicePayrollFee: boolean;
  vatPayrollFeeRate: number;
  vatPayrollFeeRateCode: Option<string> | null;
  invoicePayrollFeeDescription: string;
  invoicingMethod: PayrollFeeMethodEnum;
  feePerEmployee: string;
  tieredFees: Array<TieredFee>;
  needSendMail: boolean;
  invoiceTemplate: Option<string> | null;
  department: Option<string> | null;
  invoicePaymentModel: Option<string> | null;
  invoiceMethod: Option<string> | null;
  invoiceForEmployerPRSI: boolean;
  vatEmployerPRSIRate: number;
  vatEmployerPRSIRateCode: Option<string> | null;
  invoiceForEmployerPRSIDescription: string;
  invoiceForUSC: boolean;
  vatUSCRate: number;
  vatUSCRateCode: Option<string> | null;
  invoiceForUSCDescription: string;
};

const RaiseERCostModal: React.FC<ModalProps> = ({
  isOpen,
  toggle,
  refetch,
}: ModalProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // NX-5228: country-only twin of `isUK`, replacing the `!isBureau || isUK`
  // gate. That OR was true for umbrella-Ireland too, leaking UK-only fields
  // into an Irish record. Same rename SettingsGeneral.tsx already uses.
  const isUKCountry = payrollCountry === UNITED_KINGDOM;
  const shouldGetInvoiceSettingFrom = isBureau ? 'companyRef' : 'agencyRef';

  const [form] = Form.useForm<FormValues>();
  const watchValues = Form.useWatch([], form);

  const [openTieredFeeTable, setOpenTieredFeeTable] = useState(false);
  const toggleTieredFeeTable = () => setOpenTieredFeeTable(!openTieredFeeTable);

  const { run: raiseInvoice, loading: isRaiseInvoicePending } =
    useRaiseInvoice();

  const onFinish: FormProps['onFinish'] = async () => {
    const values: FormValues = form.getFieldsValue(true);
    let params: RaiseInvoiceParams = {} as RaiseInvoiceParams;
    if (!isBureau) {
      params = {
        companyId: (values.companyRef as any)?.value,
        agencyId: (values.agencyRef as any)?.value,
        raiseInvoicePEO: values.raiseInvoicePEO,
        invoicedDate: moment(values.invoicedDate).toISOString(),
        periodFrom: moment(values.periodFrom).toISOString(),
        periodTo: moment(values.periodTo).toISOString(),
        invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
        invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
        dueDate: toNumber(values.dueDate),
        rule: values.rule?.value || null,
        invoiceForSalary: values.invoiceForSalary,
        vatSalaryRate: values.vatSalaryRate ?? null,
        vatSalaryRateCode: values.vatSalaryRateCode
          ? {
              label: values.vatSalaryRateCode.label,
              value: values.vatSalaryRateCode.value,
              VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
              VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceForSalaryDescription: values.invoiceForSalaryDescription ?? null,
        invoiceForERNI: isIreland
          ? values.invoiceForEmployerPRSI
          : values.invoiceForERNI,
        vatERNIRate: isIreland
          ? values.vatEmployerPRSIRate ?? undefined
          : values.vatERNIRate,
        vatERNIRateCode: values.vatERNIRateCode
          ? {
              label: values.vatERNIRateCode.label,
              value: values.vatERNIRateCode.value,
              VATRate: values.vatERNIRateCode.fullData?.VATRate || '',
              VATCode: values.vatERNIRateCode.fullData?.VATCode || '',
            }
          : null,
        erNIPercent: isIreland ? 100 : values.erNIPercent ?? null,
        invoiceForERNIDescription: isIreland
          ? values.invoiceForEmployerPRSIDescription
          : values.invoiceForERNIDescription,
        invoiceERPension: values.invoiceERPension,
        vatERPensionRate: values.vatERPensionRate ?? null,
        vatERPensionRateCode: values.vatERPensionRateCode
          ? {
              label: values.vatERPensionRateCode.label,
              value: values.vatERPensionRateCode.value,
              VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
              VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceERPensionDescription: values.invoiceERPensionDescription ?? null,
        invoiceAppLevy: isIreland ? false : values.invoiceAppLevy,
        vatAppLevyRate: values.vatAppLevyRate ?? null,
        vatAppLevyRateCode: values.vatAppLevyRateCode
          ? {
              label: values.vatAppLevyRateCode.label,
              value: values.vatAppLevyRateCode.value,
              VATRate: values.vatAppLevyRateCode.fullData?.VATRate || '',
              VATCode: values.vatAppLevyRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceAppLevyDescription: values.invoiceAppLevyDescription ?? null,
        invoicePayrollFee: values.invoicePayrollFee,
        vatPayrollFeeRate: values.vatPayrollFeeRate ?? null,
        vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
          ? {
              label: values.vatPayrollFeeRateCode.label,
              value: values.vatPayrollFeeRateCode.value,
              VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
              VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
            }
          : null,
        invoicePayrollFeeDescription:
          values.invoicePayrollFeeDescription ?? null,
        invoicingMethod: (values.invoicingMethod as any)?.value || null,
        feePerEmployee: values.feePerEmployee,
        tieredFees: values.tieredFees ?? null,
        invoiceTemplate: values?.invoiceTemplate?.value || 'DEFAULT',
        needSendMail: values.needSendMail,
      };
    } else if (isUK) {
      params = {
        companyId: (values.companyRef as any)?.value,
        raiseInvoicePEO: values.raiseInvoicePEO,
        invoicedDate: moment(values.invoicedDate).toISOString(),
        periodFrom: moment(values.periodFrom).toISOString(),
        periodTo: moment(values.periodTo).toISOString(),
        invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
        invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
        dueDate: toNumber(values.dueDate),
        rule: values.rule?.value || null,
        invoiceForSalary: values.invoiceForSalary,
        vatSalaryRate: values.vatSalaryRate ?? null,
        vatSalaryRateCode: values.vatSalaryRateCode
          ? {
              label: values.vatSalaryRateCode.label,
              value: values.vatSalaryRateCode.value,
              VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
              VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceForSalaryDescription: values.invoiceForSalaryDescription ?? null,
        invoiceForERNI: values.invoiceForERNI ?? undefined,
        vatERNIRate: values.vatERNIRate ?? null,
        vatERNIRateCode: values.vatERNIRateCode
          ? {
              label: values.vatERNIRateCode.label,
              value: values.vatERNIRateCode.value,
              VATRate: values.vatERNIRateCode.fullData?.VATRate || '',
              VATCode: values.vatERNIRateCode.fullData?.VATCode || '',
            }
          : null,
        erNIPercent: values.erNIPercent ?? null,
        invoiceForERNIDescription: values.invoiceForERNIDescription ?? null,
        invoiceERPension: values.invoiceERPension,
        vatERPensionRate: values.vatERPensionRate ?? null,
        vatERPensionRateCode: values.vatERPensionRateCode
          ? {
              label: values.vatERPensionRateCode.label,
              value: values.vatERPensionRateCode.value,
              VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
              VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceERPensionDescription: values.invoiceERPensionDescription ?? null,
        invoiceAppLevy: values.invoiceAppLevy,
        vatAppLevyRate: values.vatAppLevyRate ?? null,
        vatAppLevyRateCode: values.vatAppLevyRateCode
          ? {
              label: values.vatAppLevyRateCode.label,
              value: values.vatAppLevyRateCode.value,
              VATRate: values.vatAppLevyRateCode.fullData?.VATRate || '',
              VATCode: values.vatAppLevyRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceAppLevyDescription: values.invoiceAppLevyDescription ?? null,
        invoicePayrollFee: values.invoicePayrollFee,
        vatPayrollFeeRate: values.vatPayrollFeeRate ?? null,
        vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
          ? {
              label: values.vatPayrollFeeRateCode.label,
              value: values.vatPayrollFeeRateCode.value,
              VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
              VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
            }
          : null,
        invoicePayrollFeeDescription:
          values.invoicePayrollFeeDescription ?? null,
        invoicingMethod: (values.invoicingMethod as any)?.value || null,
        feePerEmployee: values.feePerEmployee,
        tieredFees: values.tieredFees ?? null,
        invoiceTemplate: values?.invoiceTemplate?.value || null,
        needSendMail: values.needSendMail,
        invoicePaymentModel: [PAYMENT_MODEL_ENUM.PAYEE],
      };
    } else if (isIreland) {
      params = {
        companyId: (values.companyRef as any)?.value,
        raiseInvoicePEO: values.raiseInvoicePEO,
        departmentId: (values.department as any)?.value || null,
        invoicedDate: moment(values.invoicedDate).toISOString(),
        periodFrom: moment(values.periodFrom).toISOString(),
        periodTo: moment(values.periodTo).toISOString(),
        invoicePaymentModel:
          values.invoicePaymentModel?.value === 'ALL'
            ? [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.PAYEE]
            : values.invoicePaymentModel?.value
              ? [values.invoicePaymentModel?.value]
              : [],
        invoiceMethod: values.invoiceMethod?.value || undefined,
        invoiceRaisedAfter: values.invoiceRaisedAfter?.value || null,
        invoiceRaisedMethod: values.invoiceRaisedMethod?.value || null,
        dueDate: toNumber(values.dueDate),
        rule: values.rule?.value || null,
        invoiceForSalary: values.invoiceForSalary,
        vatSalaryRate: values.vatSalaryRate ?? null,
        vatSalaryRateCode: values.vatSalaryRateCode
          ? {
              label: values.vatSalaryRateCode.label,
              value: values.vatSalaryRateCode.value,
              VATRate: values.vatSalaryRateCode.fullData?.VATRate || '',
              VATCode: values.vatSalaryRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceForSalaryDescription: values.invoiceForSalaryDescription ?? '',
        invoiceForEmployerPRSI: values.invoiceForEmployerPRSI ?? '',
        vatEmployerPRSIRate: values.vatEmployerPRSIRate ?? null,
        vatEmployerPRSIRateCode: values.vatEmployerPRSIRateCode
          ? {
              label: values.vatEmployerPRSIRateCode.label,
              value: values.vatEmployerPRSIRateCode.value,
              VATRate: values.vatEmployerPRSIRateCode.fullData?.VATRate || '',
              VATCode: values.vatEmployerPRSIRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceForEmployerPRSIDescription:
          values.invoiceForEmployerPRSIDescription ?? null,
        invoiceERPension: values.invoiceERPension,
        vatERPensionRate: values.vatERPensionRate ?? null,
        vatERPensionRateCode: values.vatERPensionRateCode
          ? {
              label: values.vatERPensionRateCode.label,
              value: values.vatERPensionRateCode.value,
              VATRate: values.vatERPensionRateCode.fullData?.VATRate || '',
              VATCode: values.vatERPensionRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceERPensionDescription: values.invoiceERPensionDescription ?? null,
        invoiceForUSC: values.invoiceForUSC ?? '',
        vatUSCRate: values.vatUSCRate ?? null,
        vatUSCRateCode: values.vatUSCRateCode
          ? {
              label: values.vatUSCRateCode.label,
              value: values.vatUSCRateCode.value,
              VATRate: values.vatUSCRateCode.fullData?.VATRate || '',
              VATCode: values.vatUSCRateCode.fullData?.VATCode || '',
            }
          : null,
        invoiceForUSCDescription: values.invoiceForUSCDescription ?? null,
        invoicePayrollFee: values.invoicePayrollFee,
        vatPayrollFeeRate: values.vatPayrollFeeRate ?? null,
        vatPayrollFeeRateCode: values.vatPayrollFeeRateCode
          ? {
              label: values.vatPayrollFeeRateCode.label,
              value: values.vatPayrollFeeRateCode.value,
              VATRate: values.vatPayrollFeeRateCode.fullData?.VATRate || '',
              VATCode: values.vatPayrollFeeRateCode.fullData?.VATCode || '',
            }
          : null,
        invoicePayrollFeeDescription:
          values.invoicePayrollFeeDescription ?? null,
        invoicingMethod: (values.invoicingMethod as any)?.value || null,
        feePerEmployee: values.feePerEmployee,
        tieredFees: values.tieredFees ?? null,
        invoiceTemplate: values?.invoiceTemplate?.value || 'DEFAULT',
        needSendMail: values.needSendMail,
      };
    }
    const res = await raiseInvoice(params);
    if (res?.data) window.open(res.data, '_blank');
    showMessage(
      res?.errorMessage
        ? { type: 'error', content: res.errorMessage }
        : {
            type: 'success',
            content: messages['invoices.createInvoiceSuccess'],
          },
    );
    form.resetFields();
    toggle();
    refetch();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // noop - form validation handles error display
  };

  const handleSetInvoiceSettingValuesByCompanyOrAgency = (
    fieldname: string,
    value: any,
  ) => {
    if (fieldname !== shouldGetInvoiceSettingFrom) {
      form.setFieldsValue({
        [fieldname]: value,
      });
      return;
    }
    const invoiceSetting = value?.fullData?.invoiceSetting;
    const values: any = {
      [fieldname]: value,
      raiseInvoicePEO: invoiceSetting?.raiseInvoicePEO ?? null,
      invoiceRaisedAfter:
        defaultInvoiceRaisedAfterOptions.find(
          (item) => item.value === invoiceSetting?.invoiceRaisedAfter,
        ) || null,
      invoiceRaisedMethod:
        defaultInvoiceRaisedMethodOptions.find(
          (item) => item.value === invoiceSetting?.invoiceRaisedMethod,
        ) || null,
      dueDate: invoiceSetting?.dueDate,
      rule:
        defaultRuleInvoicingMethod.find(
          (item) => item.value === invoiceSetting?.rule,
        ) || null,
      invoiceForSalary: invoiceSetting?.invoiceForSalary ?? null,
      vatSalaryRate: invoiceSetting?.vatSalaryRate ?? null,
      vatSalaryRateCode: invoiceSetting?.vatSalaryRateCode
        ? {
            label: invoiceSetting.vatSalaryRateCode.label,
            value: invoiceSetting.vatSalaryRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatSalaryRateCode.VATRate,
              VATCode: invoiceSetting.vatSalaryRateCode.VATCode,
            },
          }
        : null,
      invoiceForSalaryDescription:
        invoiceSetting?.invoiceForSalaryDescription ?? null,
      invoiceForERNI: invoiceSetting?.invoiceForERNI ?? null,
      erNIPercent: invoiceSetting?.erNIPercent ?? null,
      vatERNIRate: invoiceSetting?.vatERNIRate ?? null,
      vatERNIRateCode: invoiceSetting?.vatERNIRateCode
        ? {
            label: invoiceSetting.vatERNIRateCode.label,
            value: invoiceSetting.vatERNIRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatERNIRateCode.VATRate,
              VATCode: invoiceSetting.vatERNIRateCode.VATCode,
            },
          }
        : null,
      invoiceForERNIDescription:
        invoiceSetting?.invoiceForERNIDescription ?? null,
      invoiceERPension: invoiceSetting?.invoiceERPension ?? null,
      vatERPensionRate: invoiceSetting?.vatERPensionRate ?? null,
      vatERPensionRateCode: invoiceSetting?.vatERPensionRateCode
        ? {
            label: invoiceSetting.vatERPensionRateCode.label,
            value: invoiceSetting.vatERPensionRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatERPensionRateCode.VATRate,
              VATCode: invoiceSetting.vatERPensionRateCode.VATCode,
            },
          }
        : null,
      invoiceERPensionDescription:
        invoiceSetting?.invoiceERPensionDescription ?? null,
      invoiceAppLevy: invoiceSetting?.invoiceAppLevy ?? null,
      vatAppLevyRate: invoiceSetting?.vatAppLevyRate ?? null,
      vatAppLevyRateCode: invoiceSetting?.vatAppLevyRateCode
        ? {
            label: invoiceSetting.vatAppLevyRateCode.label,
            value: invoiceSetting.vatAppLevyRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatAppLevyRateCode.VATRate,
              VATCode: invoiceSetting.vatAppLevyRateCode.VATCode,
            },
          }
        : null,
      invoiceAppLevyDescription:
        invoiceSetting?.invoiceAppLevyDescription ?? null,
      invoicePayrollFee: invoiceSetting?.invoicePayrollFee ?? null,
      vatPayrollFeeRate: invoiceSetting?.vatPayrollFeeRate ?? null,
      vatPayrollFeeRateCode: invoiceSetting?.vatPayrollFeeRateCode
        ? {
            label: invoiceSetting.vatPayrollFeeRateCode.label,
            value: invoiceSetting.vatPayrollFeeRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatPayrollFeeRateCode.VATRate,
              VATCode: invoiceSetting.vatPayrollFeeRateCode.VATCode,
            },
          }
        : null,
      invoicePayrollFeeDescription:
        invoiceSetting?.invoicePayrollFeeDescription ?? null,
      invoicingMethod: invoiceSetting?.invoicingMethod
        ? (defaultPayrollFreeMethod.find(
            (item) => item.value === invoiceSetting.invoicingMethod,
          ) as any) || undefined
        : undefined,
      feePerEmployee: invoiceSetting?.feePerEmployee
        ? String(invoiceSetting.feePerEmployee)
        : undefined,
      tieredFees: invoiceSetting?.tieredFees ?? null,
      invoiceTemplate:
        defaultInvoiceTemplateOptions.find(
          (item) => item.value === invoiceSetting?.invoiceTemplate,
        ) || null,
      invoicePaymentModel:
        invoiceSetting?.invoicePaymentModel?.length === 2
          ? invoicePaymentModelOptions?.find((item) => item.value === 'ALL')
          : invoicePaymentModelOptions?.find(
              (item) => item.value === invoiceSetting?.invoicePaymentModel?.[0],
            ) || null,
      invoiceMethod:
        invoiceMethodOptions.find(
          (item) => item.value === invoiceSetting?.invoiceMethod,
        ) || null,
      invoiceForEmployerPRSI: invoiceSetting?.invoiceForEmployerPRSI ?? null,
      vatEmployerPRSIRate: invoiceSetting?.vatEmployerPRSIRate ?? null,
      vatEmployerPRSIRateCode: invoiceSetting?.vatEmployerPRSIRateCode
        ? {
            label: invoiceSetting.vatEmployerPRSIRateCode.label,
            value: invoiceSetting.vatEmployerPRSIRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatEmployerPRSIRateCode.VATRate,
              VATCode: invoiceSetting.vatEmployerPRSIRateCode.VATCode,
            },
          }
        : null,
      invoiceForEmployerPRSIDescription:
        invoiceSetting?.invoiceForEmployerPRSIDescription ?? null,
      invoiceForUSC: invoiceSetting?.invoiceForUSC ?? null,
      vatUSCRate: invoiceSetting?.vatUSCRate ?? null,
      vatUSCRateCode: invoiceSetting?.vatUSCRateCode
        ? {
            label: invoiceSetting.vatUSCRateCode.label,
            value: invoiceSetting.vatUSCRateCode.value,
            fullData: {
              VATRate: invoiceSetting.vatUSCRateCode.VATRate,
              VATCode: invoiceSetting.vatUSCRateCode.VATCode,
            },
          }
        : null,
      invoiceForUSCDescription:
        invoiceSetting?.invoiceForUSCDescription ?? null,
    };
    form.setFieldsValue(values);
  };

  const departmentOptions =
    map((watchValues?.companyRef as any)?.fullData?.departments, (d) => ({
      label: d?.departmentName,
      value: d?.id,
    })) || [];

  const handleSubmitRaiseInvoice = (needSendMail = false) => {
    form.setFieldValue('needSendMail', needSendMail);
    form.submit();
  };

  return (
    <AppModalContainer
      title={messages['invoices.generateInvoice']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.action'],
          buttonType: 'primary',
          onClick: () => {},
          actionType: 'dropdown',
          menu: {
            items: [
              {
                key: 'raiseAndEmail',
                label: messages['common.raiseAndEmail'],
                onClick: () => handleSubmitRaiseInvoice(true),
              },
              {
                key: 'raiseInvoiceOnly',
                label: messages['common.raiseInvoiceOnly'],
                onClick: () => handleSubmitRaiseInvoice(false),
              },
            ],
          },
          rightIcon: <DownOutlined />,
          loading: isRaiseInvoicePending,
        },
      ]}
      width={1200}
      minHeight="0px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <div className="grid grid-cols-2 gap-x-[24px] max-sm:grid-cols-1">
          <Form.Item
            key={'companyRef'}
            label={isBureau ? messages['common.bureau.companyName'] : messages['common.companyName']}
            name={'companyRef'}
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <AppSelectSync
              placeholder={messages['form.selectCompany']}
              apiType={isBureau ? 'searchBureauEmployer' : 'searchCompany'}
              type="company"
              onChangeValue={(value: any) => {
                handleSetInvoiceSettingValuesByCompanyOrAgency(
                  'companyRef',
                  value,
                );
              }}
              {...(isBureau
                ? { paramFilter: { hasSettingRaiseInvoice: true } }
                : {})}
            />
          </Form.Item>
          {!isBureau && (
            <Form.Item
              key={'agencyRef'}
              label={messages['common.agencyName']}
              name={'agencyRef'}
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseSelect'],
                },
              ]}
            >
              <AppSelectSync
                placeholder={messages['form.selectAgency']}
                apiType="searchAgency"
                type="agency"
                onChangeValue={(value: any) => {
                  handleSetInvoiceSettingValuesByCompanyOrAgency(
                    'agencyRef',
                    value,
                  );
                }}
                paramFilter={{ hasSettingRaiseInvoice: true }}
              />
            </Form.Item>
          )}
          <Form.Item
            label={messages['invoices.raiseInvoicePEO']}
            name="raiseInvoicePEO"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          {isIreland && (
            <>
              <Form.Item
                label={messages['common.department']}
                name="department"
              >
                <AppSelect
                  options={departmentOptions}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={messages['common.invoicedDate']}
            name="invoicedDate"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
            />
          </Form.Item>
          <div
            className={clsx('grid grid-cols-2 gap-[24px] max-sm:grid-cols-1', {
              'col-span-2': !isBureau || isIreland,
            })}
          >
            <Form.Item
              label={messages['common.periodFrom']}
              name="periodFrom"
              rules={[
                { required: true, message: messages['form.pleaseSelect'] },
              ]}
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.periodTo']}
              name="periodTo"
              rules={[
                { required: true, message: messages['form.pleaseSelect'] },
              ]}
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
          </div>
          {((!isBureau &&
            watchValues?.companyRef?.value &&
            watchValues?.agencyRef?.value) ||
            (isBureau && watchValues?.companyRef?.value)) && (
            <>
              {isUKCountry && (
                <>
                  {watchValues?.raiseInvoicePEO && (
                    <>
                      <Form.Item
                        label={messages['invoices.invoiceRaisedAfter']}
                        name="invoiceRaisedAfter"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultInvoiceRaisedAfterOptions}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                      <Form.Item
                        label={messages['invoices.invoiceRaisedMethod']}
                        name="invoiceRaisedMethod"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultInvoiceRaisedMethodOptions}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item
                    label={messages['common.dueDate']}
                    name="dueDate"
                    dependencies={['rule']}
                    rules={[
                      { required: true, message: messages['form.pleaseEnter'] },
                      invoiceDueDateRule(messages),
                    ]}
                  >
                    <AppRateInput
                      placeholder={messages['form.enter']}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages['common.rule']}
                    name="rule"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={defaultRuleInvoicingMethod}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoiceForSalary']}
                      name="invoiceForSalary"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForSalary']}
                        name="vatSalaryRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatSalaryRateCode', value);
                            form.setFieldValue(
                              'vatSalaryRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    label={messages['invoices.invoiceForSalaryDescription']}
                    name="invoiceForSalaryDescription"
                    rules={[
                      {
                        required: watchValues?.invoiceForSalary,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoiceForERNI']}
                      name="invoiceForERNI"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForERNI']}
                        name="vatERNIRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatERNIRateCode', value);
                            form.setFieldValue(
                              'vatERNIRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.erNIPercent']}
                      name="erNIPercent"
                      rules={[
                        {
                          required: watchValues?.invoiceForERNI,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRateInput
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                    <Form.Item
                      label={messages['invoices.invoiceForERNIDescription']}
                      name="invoiceForERNIDescription"
                      rules={[
                        {
                          required: watchValues?.invoiceForERNI,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoiceERPension']}
                      name="invoiceERPension"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForERPension']}
                        name="vatERPensionRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatERPensionRateCode', value);
                            form.setFieldValue(
                              'vatERPensionRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    label={messages['invoices.invoiceERPensionDescription']}
                    name="invoiceERPensionDescription"
                    rules={[
                      {
                        required: watchValues?.invoiceERPension,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoiceAppLevy']}
                      name="invoiceAppLevy"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForAppLevy']}
                        name="vatAppLevyRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatAppLevyRateCode', value);
                            form.setFieldValue(
                              'vatAppLevyRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    label={messages['invoices.invoiceAppLevyDescription']}
                    name="invoiceAppLevyDescription"
                    rules={[
                      {
                        required: watchValues?.invoiceAppLevy,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoicePayrollFee']}
                      name="invoicePayrollFee"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForPayrollFee']}
                        name="vatPayrollFeeRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatPayrollFeeRateCode', value);
                            form.setFieldValue(
                              'vatPayrollFeeRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    label={messages['invoices.invoicePayrollFeeDescription']}
                    name="invoicePayrollFeeDescription"
                    rules={[
                      {
                        required: watchValues?.invoicePayrollFee,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </>
              )}
              {isIreland && (
                <>
                  <Form.Item
                    label={messages['common.paymentModel']}
                    name="invoicePaymentModel"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={invoicePaymentModelOptions}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages['common.invoiceMethod']}
                    name="invoiceMethod"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={invoiceMethodOptions}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <>
                      <Form.Item
                        label={messages['invoices.invoiceRaisedAfter']}
                        name="invoiceRaisedAfter"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultInvoiceRaisedAfterOptions}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                      <Form.Item
                        label={messages['invoices.invoiceRaisedMethod']}
                        name="invoiceRaisedMethod"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultInvoiceRaisedMethodOptions}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item
                    label={messages['common.dueDate']}
                    name="dueDate"
                    dependencies={['rule']}
                    rules={[
                      { required: true, message: messages['form.pleaseEnter'] },
                      invoiceDueDateRule(messages),
                    ]}
                  >
                    <AppRateInput
                      placeholder={messages['form.enter']}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages['common.rule']}
                    name="rule"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={defaultRuleInvoicingMethod}
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                  {watchValues?.invoicePaymentModel?.value !==
                    paymentModels.CIS && (
                    <>
                      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                        <Form.Item
                          label={messages['invoices.invoiceForSalary']}
                          name="invoiceForSalary"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseEnter'],
                            },
                          ]}
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                        {watchValues?.raiseInvoicePEO && (
                          <Form.Item
                            label={messages['common.VATRateForSalary']}
                            name="vatSalaryRateCode"
                          >
                            <AppSelectSync
                              type="VATCode"
                              apiType="searchVATCode"
                              placeholder={
                                messages['assignment.rate.selectVatRate']
                              }
                              style={{ width: '100%' }}
                              onChangeValue={(value: any) => {
                                form.setFieldValue('vatSalaryRateCode', value);
                                form.setFieldValue(
                                  'vatSalaryRate',
                                  Number.parseFloat(value?.fullData?.VATRate),
                                );
                              }}
                            />
                          </Form.Item>
                        )}
                      </div>
                      <Form.Item
                        label={messages['invoices.invoiceForSalaryDescription']}
                        name="invoiceForSalaryDescription"
                        rules={[
                          {
                            required: watchValues?.invoiceForSalary,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                        <Form.Item
                          label={messages['invoices.invoiceForEmployerPRSI']}
                          name="invoiceForEmployerPRSI"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseEnter'],
                            },
                          ]}
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                        {watchValues?.raiseInvoicePEO && (
                          <Form.Item
                            label={messages['common.VATRateForEmployerPRSI']}
                            name="vatEmployerPRSIRateCode"
                          >
                            <AppSelectSync
                              type="VATCode"
                              apiType="searchVATCode"
                              placeholder={
                                messages['assignment.rate.selectVatRate']
                              }
                              style={{ width: '100%' }}
                              onChangeValue={(value: any) => {
                                form.setFieldValue(
                                  'vatEmployerPRSIRateCode',
                                  value,
                                );
                                form.setFieldValue(
                                  'vatEmployerPRSIRate',
                                  Number.parseFloat(value?.fullData?.VATRate),
                                );
                              }}
                            />
                          </Form.Item>
                        )}
                      </div>
                      <Form.Item
                        label={
                          messages['invoices.invoiceForEmployerPRSIDescription']
                        }
                        name="invoiceForEmployerPRSIDescription"
                        rules={[
                          {
                            required: watchValues?.invoiceForEmployerPRSI,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                        <Form.Item
                          label={messages['invoices.invoiceERPension']}
                          name="invoiceERPension"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseEnter'],
                            },
                          ]}
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                        {watchValues?.raiseInvoicePEO && (
                          <Form.Item
                            label="VAT rate for ER Pension %"
                            name="vatERPensionRateCode"
                          >
                            <AppSelectSync
                              type="VATCode"
                              apiType="searchVATCode"
                              placeholder={
                                messages['assignment.rate.selectVatRate']
                              }
                              style={{ width: '100%' }}
                              onChangeValue={(value: any) => {
                                form.setFieldValue(
                                  'vatERPensionRateCode',
                                  value,
                                );
                                form.setFieldValue(
                                  'vatERPensionRate',
                                  Number.parseFloat(value?.fullData?.VATRate),
                                );
                              }}
                            />
                          </Form.Item>
                        )}
                      </div>
                      <Form.Item
                        label={messages['invoices.invoiceERPensionDescription']}
                        name="invoiceERPensionDescription"
                        rules={[
                          {
                            required: watchValues?.invoiceERPension,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                      {isBureau && <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                        <Form.Item
                          label={messages['invoices.invoiceForUSC']}
                          name="invoiceForUSC"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseEnter'],
                            },
                          ]}
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                        {watchValues?.raiseInvoicePEO && (
                          <Form.Item
                            label={messages['common.VATRateForUSC']}
                            name="vatUSCRateCode"
                          >
                            <AppSelectSync
                              type="VATCode"
                              apiType="searchVATCode"
                              placeholder={
                                messages['assignment.rate.selectVatRate']
                              }
                              style={{ width: '100%' }}
                              onChangeValue={(value: any) => {
                                form.setFieldValue('vatUSCRateCode', value);
                                form.setFieldValue(
                                  'vatUSCRate',
                                  Number.parseFloat(value?.fullData?.VATRate),
                                );
                              }}
                            />
                          </Form.Item>
                        )}
                      </div>}
                      {isBureau && <Form.Item
                        label={messages['invoices.invoiceForUSCDescription']}
                        name="invoiceForUSCDescription"
                        rules={[
                          {
                            required: watchValues?.invoiceForUSC,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>}
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                    <Form.Item
                      label={messages['invoices.invoicePayrollFee']}
                      name="invoicePayrollFee"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                    {watchValues?.raiseInvoicePEO && (
                      <Form.Item
                        label={messages['common.VATRateForPayrollFee']}
                        name="vatPayrollFeeRateCode"
                      >
                        <AppSelectSync
                          type="VATCode"
                          apiType="searchVATCode"
                          placeholder={
                            messages['assignment.rate.selectVatRate']
                          }
                          style={{ width: '100%' }}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('vatPayrollFeeRateCode', value);
                            form.setFieldValue(
                              'vatPayrollFeeRate',
                              Number.parseFloat(value?.fullData?.VATRate),
                            );
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    label={messages['invoices.invoicePayrollFeeDescription']}
                    name="invoicePayrollFeeDescription"
                    rules={[
                      {
                        required: watchValues?.invoicePayrollFee,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </>
              )}
              {watchValues?.raiseInvoicePEO && (
                <Form.Item
                  label={messages['common.invoiceTemplate']}
                  name="invoiceTemplate"
                >
                  <AppSelect
                    options={defaultInvoiceTemplateOptions}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
              )}
              <div className="flex gap-4">
                <Form.Item
                  className="flex-1"
                  label={messages['invoices.invoicingMethod']}
                  name="invoicingMethod"
                  dependencies={['raiseInvoicePEO', 'invoiceRaisedAfter']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                    afterDraftFeeMethodRule(
                      messages,
                      'raiseInvoicePEO',
                      'invoiceRaisedAfter',
                    ),
                    tieredFeesRule(messages),
                  ]}
                >
                  <AppSelect
                    options={payrollFeeMethodOptions(
                      defaultPayrollFreeMethod,
                      watchValues?.raiseInvoicePEO,
                      watchValues?.invoiceRaisedAfter,
                    )}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                {(watchValues?.invoicingMethod as any)?.value ===
                  PayrollFeeMethodEnum.TIERED_FEE && (
                  <div className="flex items-center">
                    <Tooltip title={messages['common.tieredFeeTable']}>
                      <TableOutlined
                        className="cursor-pointer"
                        onClick={toggleTieredFeeTable}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
              {(watchValues?.invoicingMethod as any)?.value ===
                PayrollFeeMethodEnum.FIXED_FEE_PER_EMPLOYEE && (
                <Form.Item
                  key={'feePerEmployee'}
                  label={messages['invoices.feePerEmployee']}
                  name={'feePerEmployee'}
                  rules={[
                    {
                      required: true,
                      message: messages['common.pleaseEnter'],
                    },
                  ]}
                >
                  <AppCurrencyInput
                    prefix={showCurrencyByCountry(payrollCountry)}
                    placeholder={messages['common.enter']}
                  />
                </Form.Item>
              )}
              <Form.Item name="tieredFees" className="hidden">
                {openTieredFeeTable && (
                  <TieredFeeTableModal
                    isOpen={openTieredFeeTable}
                    toggle={toggleTieredFeeTable}
                    defaultValues={{
                      tieredFees: watchValues?.tieredFees,
                    }}
                    onSubmit={(values) => {
                      form.setFieldValue('tieredFees', values);
                      // The fee method rule counts the tiers.
                      form.validateFields(['invoicingMethod']).catch(() => {});
                    }}
                  />
                )}
              </Form.Item>
            </>
          )}
        </div>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default RaiseERCostModal;
